import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import LanguageToggle from "../components/LanguageToggle";
import { BrandMark } from "../components/Logo";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 6) { setErr(t("auth.reset_min")); return; }
    if (pwd !== pwd2) { setErr(t("auth.reset_mismatch")); return; }
    if (!token) { setErr(t("auth.reset_no_token")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center grain relative px-4">
      <div className="absolute top-6 right-6 z-10"><LanguageToggle /></div>
      <Link to="/" className="absolute top-6 left-6 z-10" data-testid="brand-link">
        <BrandMark />
      </Link>
      <div className="w-full max-w-md glass rounded-3xl p-8 lg:p-10 anim-stagger relative">
        <h1 className="text-3xl font-extrabold text-white tracking-tight">{t("auth.reset_title")}</h1>
        <p className="mt-2 text-sm text-slate-400">{t("auth.reset_sub")}</p>

        {done ? (
          <div className="mt-8 rounded-xl bg-emerald-400/10 border border-emerald-400/20 p-4 text-sm text-emerald-300 inline-flex items-center gap-2" data-testid="reset-success">
            <CheckCircle2 className="h-4 w-4"/> {t("auth.reset_done")}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("auth.reset_new")}</label>
              <input data-testid="reset-pwd" type="password" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} className="input-dark mt-1.5" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("auth.reset_confirm")}</label>
              <input data-testid="reset-pwd2" type="password" required minLength={6} value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="input-dark mt-1.5" />
            </div>
            {err && <div className="text-rose-400 text-sm" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="reset-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("auth.reset_submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
